import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  input,
} from "@angular/core";
import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { MatDialog } from "@angular/material/dialog";
import { Observable, of, skipWhile } from "rxjs";
import { catchError, map, shareReplay } from "rxjs/operators";
import {
  FormAttachmentGroupTypes,
  TableFieldTypes,
} from "../../../locale/consts.en";
import { TableFieldType } from "../table-field/table-field.model";
import { LibraryDocument } from "../../library/library.model";
import { DatabaseService } from "../../shared/database.service";
import {
  ConfDialogType,
  ConfirmDialogModel,
} from "../../shared/confirm-dialog/confim-dialog.model";
import { ConfirmDialogComponent } from "../../shared/confirm-dialog/confirm-dialog.component";
import { DescriptiveFormTemplate, FormTableType } from "../forms.model";
import { FormsService } from "../forms.service";
import { ServiceChart, chartTypeMeta } from "../chart/chart.model";
import {
  ChartDesignerDialogComponent,
  ChartDesignerDialogData,
} from "../chart/chart-designer-dialog/chart-designer-dialog.component";
import { PreviewColumn } from "../chart/chart-preview";
// import * as buffer from 'buffer';
import { sortByStringWithEmptyAtEnd } from "src/app/shared/helpers/helper-functions";
import { MasterDataService } from "../../master-data/master-data-service";
import { ProductCategory } from "../../master-data/master-data.model";
import {
  DeviceAttributeFieldDialogComponent,
  DeviceAttributeFieldDialogData,
  DeviceAttributeFieldDialogResult,
} from "../item/device-attribute-field-dialog/device-attribute-field-dialog.component";
import {
  attributeKeysMissingFromCategory,
  deviceAttributeKey,
  isAttachmentOnly,
  templateFieldForms,
} from "../item/formula-variables";

// (window as any).Buffer = buffer.Buffer;

@Component({
  selector: "app-form-edit",
  templateUrl: "./form-edit.component.html",
  styleUrls: ["./form-edit.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FormEditComponent implements OnInit {
  formsService = inject(FormsService);
  private databaseService = inject(DatabaseService);
  private masterDataService = inject(MasterDataService);
  private dialog = inject(MatDialog);

  chartTypeMeta = chartTypeMeta;

  readonly editForm = input<any>(undefined); //

  FTableType = FormTableType;

  attachmentGroupTypes = FormAttachmentGroupTypes;
  productCategories: ProductCategory[] = [];

  loadedTemplates$: Observable<DescriptiveFormTemplate[]>;
  loadedDocuments$: Observable<LibraryDocument[]>;

  file: unknown;
  currentDrawingForm: FormControl = null;

  constructor() {
    const formsService = this.formsService;

    this.loadedTemplates$ = formsService.loadedForms$;
    this.loadedDocuments$ = this.databaseService
      .loadDocuments()
      .pipe(
        map((docs) => sortByStringWithEmptyAtEnd(docs, (item) => item.name)),
      );
    // .pipe(map((docs) => docs.sort((a, b) => a.name.localeCompare(b.name))));
  }

  ngOnInit() {
    this.masterDataService
      .getProductCategories()
      .pipe(skipWhile((pc) => pc == null || pc.length === 0))
      .subscribe((prodCateg) => {
        this.productCategories = prodCateg;
      });
  }

  public compareFn(c1: LibraryDocument, c2: LibraryDocument): boolean {
    return c1 && c2 && c1.uuid === c2.uuid;
  }

  addItem(section: number) {
    this.formsService.addItem(section);
  }

  deleteItem(index: number, section: number) {
    this.formsService.deleteItem(index, section);
  }

  addSection() {
    this.formsService.addSection();
  }

  deleteSection(index: number) {
    this.formsService.deleteSection(index);
  }

  addField() {
    this.formsService.addField();
  }

  isAttachmentOnly(): boolean {
    return isAttachmentOnly(this.editForm());
  }

  /** Adds "devAttr_<key>" fields for editable attributes of a category picked in a dialog. */
  addDeviceAttributeFields() {
    const data: DeviceAttributeFieldDialogData = {
      categories: this.productCategories,
      existingRenderTags: templateFieldForms(this.editForm()).map(
        (field) => field.get("renderTag")?.value as string,
      ),
      sections: this.getGroups(this.editForm()).map(
        (section) => (section.get("name")?.value as string) ?? "",
      ),
    };
    this.dialog
      .open<
        DeviceAttributeFieldDialogComponent,
        DeviceAttributeFieldDialogData,
        DeviceAttributeFieldDialogResult
      >(DeviceAttributeFieldDialogComponent, {
        width: "520px",
        maxWidth: "95vw",
        data,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result?.attributes.length > 0) {
          this.formsService.addDeviceAttributeFields(
            result.attributes,
            result.section,
          );
        }
      });
  }

  // attribute keys of the devAttr_ fields of attachment templates, by template uuid
  private deviceAttributeKeysByTemplate = new Map<string, Observable<string[]>>();
  private mismatchByAttachment = new Map<string, Observable<string[]>>();

  /**
   * The device attribute fields of a device attachment's template that aren't
   * attributes of the attachment's category at all: they would stay empty.
   * Fixed (product) attributes count, as mobile shows them read-only.
   */
  deviceAttributeMismatch(attachment: FormGroup): Observable<string[]> {
    const templateUuid = attachment.get("templateUuid")?.value as string;
    const categoryUuid = attachment.get("productCategoryUuid")?.value as string;
    if (!templateUuid || !categoryUuid) {
      return of([]);
    }
    const cacheKey = `${templateUuid}|${categoryUuid}|${this.productCategories.length}`;
    if (!this.mismatchByAttachment.has(cacheKey)) {
      const category = this.productCategories.find((c) => c.uuid === categoryUuid);
      this.mismatchByAttachment.set(
        cacheKey,
        this.templateDeviceAttributeKeys(templateUuid).pipe(
          map((keys) => attributeKeysMissingFromCategory(keys, category)),
        ),
      );
    }
    return this.mismatchByAttachment.get(cacheKey);
  }

  private templateDeviceAttributeKeys(templateUuid: string): Observable<string[]> {
    if (!this.deviceAttributeKeysByTemplate.has(templateUuid)) {
      this.deviceAttributeKeysByTemplate.set(
        templateUuid,
        this.databaseService.findFormByUuid(templateUuid).pipe(
          map((template) =>
            [
              ...(template.fields ?? []),
              ...(template.groups ?? []).flatMap((group) => group.fields ?? []),
            ]
              .map((field) => deviceAttributeKey(field.renderTag))
              .filter((key): key is string => key != null),
          ),
          catchError(() => of([])),
          shareReplay(1),
        ),
      );
    }
    return this.deviceAttributeKeysByTemplate.get(templateUuid);
  }

  deleteField(field: number) {
    this.formsService.deleteField(field);
  }

  addTableCol(table: number) {
    const newIndex = this.tableColControls(table).length;
    this.formsService.addTableColumn(table);
    // a freshly added column opens expanded so it can be filled in
    this.expandedCols.add(this.colStateKey(table, newIndex));
  }

  deleteTableCol(index: number, table: number) {
    this.formsService.deleteTableColumn(index, table);
    // collapse-state keys are positional — drop them so they don't drift
    this.clearColStateForTable(table);
  }

  // ---- table column editor: list / cards view + per-column collapse -------

  /** view mode per table index — 'list' (collapsible rows) or 'cards' (strip) */
  private tableColViews = new Map<number, "list" | "cards">();
  /** expanded column rows in list view, keyed by `${tableIndex}:${colIndex}` */
  private expandedCols = new Set<string>();

  private colStateKey(tableIndex: number, colIndex: number): string {
    return `${tableIndex}:${colIndex}`;
  }

  private clearColStateForTable(tableIndex: number): void {
    for (const key of [...this.expandedCols]) {
      if (key.startsWith(`${tableIndex}:`)) {
        this.expandedCols.delete(key);
      }
    }
  }

  private tableColControls(tableIndex: number): FormArray["controls"] {
    const tables = this.editForm().get("tables") as FormArray;
    const table = tables?.at(tableIndex) as FormGroup;
    return (table?.get("columns") as FormArray)?.controls ?? [];
  }

  colView(tableIndex: number): "list" | "cards" {
    return this.tableColViews.get(tableIndex) ?? "list";
  }

  setColView(tableIndex: number, view: "list" | "cards"): void {
    this.tableColViews.set(tableIndex, view);
  }

  isColExpanded(tableIndex: number, colIndex: number): boolean {
    return this.expandedCols.has(this.colStateKey(tableIndex, colIndex));
  }

  toggleCol(tableIndex: number, colIndex: number): void {
    const key = this.colStateKey(tableIndex, colIndex);
    if (this.expandedCols.has(key)) {
      this.expandedCols.delete(key);
    } else {
      this.expandedCols.add(key);
    }
  }

  expandAllCols(tableIndex: number): void {
    this.tableColControls(tableIndex).forEach((_, i) =>
      this.expandedCols.add(this.colStateKey(tableIndex, i)),
    );
  }

  collapseAllCols(tableIndex: number): void {
    this.clearColStateForTable(tableIndex);
  }

  colSummary(item: unknown): {
    type: string;
    name: string;
    key: string;
    width: string;
    sort: string;
  } {
    const g = item as FormGroup;
    const typeVal = g.controls["type"]?.value;
    return {
      type:
        typeVal == null
          ? $localize`(no type)`
          : TableFieldTypes[typeVal as TableFieldType] ?? String(typeVal),
      name: g.controls["name"]?.value || "",
      key: g.controls["columnKey"]?.value || "",
      width: g.controls["width"]?.value ?? "",
      sort: g.controls["sort"]?.value ?? "",
    };
  }

  // ---- charts ----------------------------------------------------------

  getCharts(table: FormGroup): FormGroup[] {
    return (table.get("charts") as FormArray)?.controls as FormGroup[];
  }

  private tableColumns(table: FormGroup): PreviewColumn[] {
    return ((table.get("columns") as FormArray)?.controls ?? [])
      .map((c) => c.value as PreviewColumn)
      .filter((c) => !!c?.columnKey)
      .map((c) => ({
        columnKey: c.columnKey,
        name: c.name || c.columnKey,
        type: c.type,
        values: (c as unknown as { values?: string[] }).values,
        valueMap: (c as unknown as { valueMap?: string }).valueMap,
      }));
  }

  private usedRenderTags(exclude?: { table: number; chart: number }): string[] {
    const tables = (this.editForm().get("tables") as FormArray).controls;
    const tags: string[] = [];
    tables.forEach((t, ti) => {
      ((t.get("charts") as FormArray)?.controls ?? []).forEach((c, ci) => {
        if (exclude && exclude.table === ti && exclude.chart === ci) {
          return;
        }
        const tag = (c.value as ServiceChart)?.renderTag;
        if (tag) {
          tags.push(tag.trim());
        }
      });
    });
    return tags;
  }

  openChartDesigner(tableIndex: number, chartIndex?: number) {
    const tables = this.editForm().get("tables") as FormArray;
    const tableGroup = tables.at(tableIndex) as FormGroup;
    const isEdit = chartIndex != null;
    const existing = isEdit
      ? ((tableGroup.get("charts") as FormArray).at(chartIndex).value as
          ServiceChart)
      : undefined;

    const data: ChartDesignerDialogData = {
      tableName: tableGroup.get("name")?.value || $localize`table`,
      columns: this.tableColumns(tableGroup),
      chart: existing,
      usedRenderTags: this.usedRenderTags(
        isEdit ? { table: tableIndex, chart: chartIndex } : undefined,
      ),
    };

    const dialogRef = this.dialog.open(ChartDesignerDialogComponent, {
      width: "1240px",
      maxWidth: "96vw",
      data,
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe((result: ServiceChart | undefined) => {
      if (!result) {
        return;
      }
      if (isEdit) {
        this.formsService.updateChart(tableIndex, chartIndex, result);
      } else {
        this.formsService.addChart(tableIndex, result);
      }
    });
  }

  deleteChart(tableIndex: number, chartIndex: number, name: string) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      maxWidth: "400px",
      data: new ConfirmDialogModel(
        $localize`Confirm action`,
        $localize`Delete chart "${name}"?`,
        ConfDialogType.CONFIRM,
      ),
    });
    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.formsService.deleteChart(tableIndex, chartIndex);
      }
    });
  }

  addTable(type: FormTableType) {
    this.formsService.addTable(type);
  }

  getFormArrayValidity(i): boolean {
    return (
      (this.editForm().controls.tables as FormArray).controls[i] as FormGroup
    )?.invalid;
  }
  deleteTable(table: number) {
    this.formsService.deleteTable(table);
  }

  addSignature() {
    this.formsService.addSignature();
  }

  deleteSignature(index: number) {
    this.formsService.deleteSignature(index);
  }

  addDrawing() {
    this.formsService.addDrawing();
  }

  deleteDrawing(index: number) {
    this.formsService.deleteDrawing(index);
  }

  addAttachment() {
    this.formsService.addAttachment();
  }

  deleteAttachment(index: number) {
    this.formsService.deleteAttachment(index);
  }

  addAttachmentGroup() {
    this.formsService.addAttachmentGroup();
  }

  deleteAttachmentGroup(index: number) {
    this.formsService.deleteAttachmentGroup(index);
  }

  addAttachmentToGroup(grIndex: number) {
    this.formsService.addAttachmentToGroup(grIndex);
  }

  deleteAttachmentFromGroup(docIndex: number, attIndex: number) {
    this.formsService.deleteAttachmentFromGroup(docIndex, attIndex);
  }

  addInteractiveAttachmentGroup() {
    this.formsService.addInteractiveAttachmentGroup();
  }

  deleteInteractiveAttachmentGroup(attIndex: number) {
    this.formsService.deleteInteractiveAttachmentGroup(attIndex);
  }

  addInteractiveAttachmentToGroup(grIndex: number) {
    this.formsService.addInteractiveAttachmentToGroup(grIndex);
  }

  deleteInteractiveAttachmentFromGroup(attGrIndex: number) {
    this.formsService.deleteInteractiveAttachmentFromGroup(attGrIndex);
  }

  addDeviceAttachmentGroup() {
    this.formsService.addDeviceAttachmentGroup();
  }

  deleteDeviceAttachmentGroup(index: number) {
    this.formsService.deleteDeviceAttachmentGroup(index);
  }

  addDeviceAttachmentToGroup(grIndex: number) {
    this.formsService.addDeviceAttachmentToGroup(grIndex);
  }

  deleteDeviceAttachmentFromGroup(docIndex: number, attIndex: number) {
    this.formsService.deleteDeviceAttachmentFromGroup(docIndex, attIndex);
  }

  addDocumentLink() {
    this.formsService.addDocumentLink();
  }

  deleteDocumentLink(index: number) {
    this.formsService.deleteDocumentLink(index);
  }

  getGroups(form: FormGroup) {
    return (form.get("groups") as FormArray).controls as FormGroup[];
  }

  getFields(form: FormGroup) {
    return (form.get("fields") as FormArray).controls;
  }

  getTables(form: FormGroup) {
    return (form.get("tables") as FormArray).controls as FormGroup[];
  }

  getSignatures(form: FormGroup) {
    return (form.get("signatures") as FormArray).controls as FormGroup[];
  }

  getDrawings(form: FormGroup) {
    return (form.get("drawings") as FormArray).controls as FormGroup[];
  }

  getAttachments(form: FormGroup) {
    return (form.get("attachments") as FormArray).controls as FormGroup[];
  }

  getAttachmentGroups(form: FormGroup) {
    return (form.get("attachmentGroups") as FormArray).controls as FormGroup[];
  }

  getInteractiveAttachmentGroups(form: FormGroup) {
    return (form.get("interactiveAttachmentGroups") as FormArray)
      .controls as FormGroup[];
  }

  getDeviceAttachmentGroups(form: FormGroup) {
    return (form.get("deviceAttachmentGroups") as FormArray)
      .controls as FormGroup[];
  }
  getDocuments(form: FormGroup) {
    return (form.get("documents") as FormArray).controls as FormGroup[];
  }

  fileChange(e: Event, form: FormControl) {
    this.file = (e.target as HTMLInputElement).files[0];
    this.uploadDocument(this.file);
    this.currentDrawingForm = form;
  }

  uploadDocument(file) {
    const fileReader = new FileReader();
    fileReader.onload = this.handleReaderLoaded.bind(this);
    fileReader.readAsBinaryString(file);
  }

  handleReaderLoaded(readerEvt) {
    const binaryString = readerEvt.target.result;
    // this.currentDrawingForm.setValue(btoa(binaryString));  //deprecated
    this.currentDrawingForm.setValue(
      Buffer.from(binaryString, "binary").toString("base64"),
    );
  }

  openInput(index) {
    document.getElementById(index).click();
  }

  changeBackground(
    $event: MatCheckboxChange,
    base64PngBackground: FormControl,
  ) {
    if ($event.checked) {
      base64PngBackground.setValidators([]);
      base64PngBackground.updateValueAndValidity();
    } else {
      base64PngBackground.setValidators([Validators.required]);
      base64PngBackground.updateValueAndValidity();
    }
  }
  //protected readonly FormArray = FormArray;

  getFormGroup(item: unknown) {
    return item as FormGroup;
  }

  getFormControl(item: unknown) {
    return item as FormControl;
  }

  getFormArray(item: unknown) {
    return item as FormArray;
  }

  protected readonly FormControl = FormControl;
}
